import React, { useState, useRef, useEffect } from "react";
import { beepAndVibrate } from "../utils/feedback";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Camera,
  Check,
  Copy,
  Pencil,
  RotateCcw,
  ScanLine,
  Upload,
} from "lucide-react";
import geminiService from "../services/gemini";
import { apiService } from "../services/apiService";
import { compressImage } from "../utils/imageCompression";
import { resolveNamaIbu } from "../utils/motherName";
import { getUploadErrorMessage } from "../utils/uploadError";
import blisterScanImg from "../assets/blister-scan.svg";
import toast from "react-hot-toast";

const MMSBlisterScreen = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [analysisMethod, setAnalysisMethod] = useState("yolo");
  const [manualCount, setManualCount] = useState("");
  const [editingCount, setEditingCount] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState(null);
  const [nameMother, setNameMother] = useState(null);
  const [confirmedNameMother, setConfirmedNameMother] = useState("");
  const [idIbu, setIdIbu] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const motherInfo = location.state?.motherInfo;
  const namaIbu = resolveNamaIbu({ nameMother, confirmedNameMother });

  // Get barcode data from navigation state
  useEffect(() => {
    if (location.state) {
      setScannedBarcode(location.state.barcode);
      if (location.state.nameMother) {
        setConfirmedNameMother(location.state.nameMother);
      }
      if (location.state.idIbu) {
        setIdIbu(location.state.idIbu);
      }
    }
  }, [location.state]);

  // Fetch name_mother when barcode is available
  useEffect(() => {
    const fetchNameMother = async () => {
      if (scannedBarcode) {
        try {
          const scanData = await apiService.scanBarcode(scannedBarcode);
          if (scanData.name_mother) {
            setNameMother(scanData.name_mother);
          }
          if (scanData.id_ibu || scanData.respondent_id) {
            setIdIbu(scanData.id_ibu || scanData.respondent_id);
          }
        } catch (error) {
          console.error("Error fetching name_mother:", error);
          // Don't show error to user, just log it
        }
      }
    };

    fetchNameMother();
  }, [scannedBarcode]);

  const resetResult = () => {
    setAiResult(null);
    setManualCount("");
    setEditingCount(false);
    setConfirmed(false);
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setSelectedImage(file);
        setPreviewUrl(URL.createObjectURL(file));
        resetResult();
      } else {
        toast.error("Please select an image file");
      }
    }
  };

  const handleRetake = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    resetResult();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const analyzeImage = async () => {
    if (!selectedImage) {
      toast.error("Please select an image first");
      return;
    }

    setLoading(true);
    try {
      let result;
      if (analysisMethod === "gemini") {
        // Standardize & compress image before sending to Gemini
        const compressedImage = await compressImage(selectedImage, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
        });
        const arrayBuffer = await compressedImage.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const geminiResult =
          await geminiService.analyzeMMSBlister(uint8Array);
        result = {
          ...geminiResult,
          method: "LLM Gemini",
        };
      } else {
        // Backend YOLO detection with automatic compression and barcode linkage
        const yoloResult =
          await apiService.countMMSBlisterYolo(
            selectedImage,
            0.25,
            scannedBarcode,
          );
        result = {
          tabletCount: yoloResult.tablet_count,
          confidence: yoloResult.confidence,
          description:
            yoloResult.description ||
            `YOLO counted ${yoloResult.tablet_count} blister_segment_1 box(es).`,
          method: "YOLO",
          annotatedImageBase64: yoloResult.annotated_image_base64,
          rawDetections: yoloResult.raw_detections,
          targetClass: yoloResult.target_class,
          targetDetections: yoloResult.target_detections,
          classCounts: yoloResult.class_counts,
          capture_id: yoloResult.capture_id || null,
          timestamp: yoloResult.timestamp || null,
          gcs_uri: yoloResult.gcs_uri || null,
          ai_metadata: yoloResult.ai_metadata || null,
        };
      }

      beepAndVibrate();

      toast.success("Analysis Completed!");
      setAiResult(result);
      setManualCount(result.tabletCount.toString());
    } catch (error) {
      console.error("Error analyzing image:", error);

      let errorMessage = getUploadErrorMessage(error);

      if (analysisMethod === "gemini" && error.message) {
        if (error.message.includes("API key")) {
          errorMessage =
            "Gemini API key is not configured. Please contact administrator.";
        } else if (
          error.message.includes("quota") ||
          error.message.includes("Quota exceeded")
        ) {
          errorMessage =
            "Gemini API quota has been exceeded. " +
            "Please contact administrator or try again later. " +
            "You can also enter the count manually.";
        } else if (error.message.includes("Empty response")) {
          errorMessage =
            "AI service returned empty response. Please try again.";
        }
      }

      toast.error(errorMessage, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedImage || !aiResult || manualCount === "") {
      toast.error("Please provide an image and tablet count");
      return;
    }

    if (!scannedBarcode) {
      toast.error("Barcode ID is required. Please scan barcode first.");
      return;
    }

    if (!namaIbu) {
      toast.error("Nama ibu diperlukan. Silakan scan ulang barcode.");
      return;
    }

    const finalCount = parseInt(manualCount, 10);
    if (isNaN(finalCount) || finalCount < 0) {
      toast.error("Please enter a valid tablet count");
      return;
    }

    setSubmitting(true);
    try {
      const captureId = aiResult.capture_id || null;
      const imageUrl = aiResult.image_url || aiResult.gcs_uri || null;
      const yoloImage = aiResult.yolo_image || null;

      // jumlah_pills keeps the AI count; a manual correction goes to jumlah_pills_koreksi
      const result = await apiService.submitCounting({
        barcode_id: scannedBarcode,
        jumlah_pills: aiResult.tabletCount,
        jumlah_pills_koreksi:
          finalCount !== aiResult.tabletCount ? finalCount : null,
        detection_type: "mms_blister",
        nama_ibu: namaIbu,
        id_ibu: idIbu || null,
        capture_id: captureId,
        ai_metadata: aiResult.ai_metadata || null,
        image_url: imageUrl,
        yolo_image: yoloImage,
      });

      toast.success("MMS blister count submitted successfully!");

      const imageAttachment = aiResult.annotatedImageBase64
        ? `data:image/jpeg;base64,${aiResult.annotatedImageBase64}`
        : previewUrl;

      navigate("/results", {
        state: {
          record: {
            barcode_id: scannedBarcode,
            detection_type: "mms_blister",
            jenis_counting: "MMS Blister",
            nama_ibu: namaIbu,
            id_ibu: idIbu || result?.id_ibu || null,
            capture_id: captureId,
            image_url: result?.image_url || imageUrl,
            yolo_image: result?.yolo_image || yoloImage,
            image: imageAttachment,
            ...result,
            jumlah_pills: finalCount,
          },
          motherInfo,
          aiResult,
          capture_id: captureId,
          image: imageAttachment,
        },
      });
    } catch (error) {
      console.error("Error submitting record:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to submit record";
      toast.error(`Failed to submit record: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const copyBarcode = async () => {
    try {
      await navigator.clipboard.writeText(scannedBarcode);
      toast.success("Barcode copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy barcode");
    }
  };

  const resultImage = aiResult?.annotatedImageBase64
    ? `data:image/jpeg;base64,${aiResult.annotatedImageBase64}`
    : previewUrl;
  const isCorrected =
    aiResult && manualCount !== "" && Number(manualCount) !== aiResult.tabletCount;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={() => navigate("/")}
              className="mr-4 p-2 text-gray-900 dark:text-gray-300 hover:text-gray-600 dark:hover:text-white"
              aria-label="Kembali"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              MMS Blister Counting
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-8">
        {/* Mother & barcode info */}
        <dl className="px-1 space-y-1 text-gray-900 dark:text-gray-200">
          <div className="flex gap-2">
            <dt>Nama Ibu :</dt>
            <dd className="break-words">{namaIbu || "-"}</dd>
          </div>
          {scannedBarcode && (
            <div className="flex items-center gap-2">
              <dt>ID MMS :</dt>
              <dd className="font-mono break-all">{scannedBarcode}</dd>
              <button
                onClick={copyBarcode}
                className="p-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                aria-label="Copy barcode"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          )}
        </dl>

        {/* Image card */}
        <div className="mt-8 rounded-xl bg-white dark:bg-gray-800 shadow-md px-6 py-8">
          {!previewUrl ? (
            // State 1: no image yet
            <div className="flex flex-col items-center text-center">
              <img src={blisterScanImg} alt="" className="h-36 w-auto" />
              <h2 className="mt-8 text-lg font-bold text-gray-900 dark:text-white">
                Add MMS Blister Image
              </h2>
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-300">
                Pastikan gambar MMS Blister terlihat jelas
              </p>
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="mt-8 w-full flex items-center justify-center gap-3 py-4 rounded-lg bg-blue-600 text-lg text-white hover:bg-blue-700"
              >
                <Camera className="h-6 w-6" />
                Ambil Foto
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="mt-4 w-full flex items-center justify-center gap-3 py-4 rounded-lg bg-gray-200 dark:bg-gray-700 text-lg text-gray-900 dark:text-gray-100 hover:bg-gray-300"
              >
                <Upload className="h-6 w-6" />
                Pilih dari Galeri
              </button>
              {/* Android Chrome often opens only the gallery without capture, so camera gets its own input */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageSelect}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                data-testid="image-input"
              />
            </div>
          ) : (
            <>
              <div className="relative mx-auto w-fit overflow-hidden rounded-lg">
                <img
                  src={aiResult ? resultImage : previewUrl}
                  alt={aiResult ? "Hasil deteksi blister" : "Blister pack"}
                  className="max-h-80 w-auto object-contain"
                />
                {loading && (
                  <div
                    role="status"
                    aria-label="Menganalisis gambar"
                    className="ai-scan-shimmer absolute inset-0"
                  >
                    <div className="animate-scan-laser" />
                  </div>
                )}
              </div>

              {!aiResult ? (
                // State 2: image added, not analyzed yet
                <div className="mt-8 space-y-4">
                  <button
                    onClick={handleRetake}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 py-4 rounded-lg bg-gray-200 dark:bg-gray-700 text-lg text-gray-900 dark:text-gray-100 hover:bg-gray-300 disabled:opacity-50"
                  >
                    <RotateCcw className="h-6 w-6" />
                    Ambil Ulang
                  </button>
                  <button
                    onClick={analyzeImage}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 py-4 rounded-lg bg-blue-600 text-lg text-white hover:bg-blue-700 disabled:opacity-70"
                  >
                    <ScanLine className="h-6 w-6" />
                    {loading ? "Analyzing..." : "Analyze"}
                  </button>
                </div>
              ) : (
                // State 3: analyzed
                <>
                <div className="mt-10 flex items-center justify-between gap-4 px-2">
                  <div>
                    <p className="text-gray-900 dark:text-gray-200">
                      Hasil Counting
                    </p>
                    {editingCount ? (
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        value={manualCount}
                        onChange={(e) => {
                          setManualCount(e.target.value);
                          setConfirmed(false);
                        }}
                        aria-label="Koreksi jumlah tablet"
                        className="mt-1 w-28 px-3 py-1 border border-slate-400 rounded-md bg-white dark:bg-gray-900 text-xl font-bold text-gray-900 dark:text-white focus:outline-none focus:border-blue-600"
                        autoFocus
                      />
                    ) : (
                      <p className="text-2xl font-bold text-green-600">
                        {manualCount} Tablet
                      </p>
                    )}
                    {isCorrected && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Hasil AI: {aiResult.tabletCount} Tablet
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setEditingCount((v) => !v)}
                    disabled={editingCount && manualCount === ""}
                    className="flex items-center gap-2 px-5 py-3 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 disabled:opacity-50"
                  >
                    {editingCount ? (
                      <>
                        <Check className="h-5 w-5" />
                        Selesai
                      </>
                    ) : (
                      <>
                        <Pencil className="h-5 w-5" />
                        Koreksi
                      </>
                    )}
                  </button>
                </div>
                {/* Wrong image already analyzed: start over and analyze again */}
                <button
                  onClick={handleRetake}
                  disabled={submitting}
                  className="mt-6 w-full flex items-center justify-center gap-3 py-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 disabled:opacity-50"
                >
                  <RotateCcw className="h-5 w-5" />
                  Ambil Ulang
                </button>
                </>
              )}
            </>
          )}
        </div>

        {!aiResult ? (
          <>
            {/* Model selection */}
            <div className="mt-10">
              <label htmlFor="analysisMethod" className="sr-only">
                Model deteksi
              </label>
              <select
                id="analysisMethod"
                value={analysisMethod}
                onChange={(e) => setAnalysisMethod(e.target.value)}
                disabled={loading}
                className="px-4 py-3 rounded-lg border-0 shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-600"
              >
                <option value="yolo">YOLO11</option>
                <option value="gemini" disabled>
                  Gemini 2.5 Flash
                </option>
              </select>
            </div>

            {/* Instructions */}
            <div className="mt-10 rounded-lg border border-cyan-300 bg-cyan-50 dark:bg-cyan-900/20 dark:border-cyan-800 px-6 py-5 text-cyan-900 dark:text-cyan-200">
              <h3 className="text-lg font-bold">Tata cara pengambilan gambar</h3>
              <ul className="mt-3 list-disc pl-5 space-y-1">
                <li>Pastikan menggunakan gambar dengan fokus jelas dan tidak blur</li>
                <li>Ambil gambar blister dari atas dan pastikan QR terlihat</li>
                <li>Gunakan flash jika terlalu gelap dan minim cahaya</li>
              </ul>
            </div>
          </>
        ) : (
          <>
            <label className="mt-12 flex items-center gap-3 px-1 text-gray-900 dark:text-gray-200">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={editingCount}
                className="h-5 w-5 rounded border-slate-400"
              />
              Saya menyatakan hasil counting sudah benar
            </label>

            <button
              onClick={handleSubmit}
              disabled={!confirmed || editingCount || submitting}
              className="mt-6 w-full py-4 rounded-lg bg-blue-600 text-lg text-white hover:bg-blue-700 disabled:bg-blue-600/30 disabled:cursor-not-allowed"
            >
              {submitting ? "Menyimpan..." : "Simpan"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MMSBlisterScreen;
